import { ACCESSIBILITY_COLOR_MODES } from 'config/accessibilityTheme.config';
import {
  DEFAULT_ACCESSIBILITY_OPTIONS,
  DEFAULT_DISPLAY_OPTIONS,
  DISPLAY_THEME_STORAGE_KEY,
  MAP_OPTIONS_STORAGE_KEY,
  CHART_OPTIONS_STORAGE_KEY,
  ACCESSIBILITY_OPTIONS_STORAGE_KEY,
  ACCESSIBILITY_DISPLAY_STYLE_BY_COLOR_MODE,
  DEFAULT_NAVIGATION_STYLE,
  NAVIGATION_STYLE_STORAGE_KEY,
  NAVIGATION_STYLES,
} from 'store/config/displayOptions.config';
import { DEFAULT_CHART_OPTIONS, normalizeChartOptions } from 'utils/charts/chartStyle.utils';
import { DEFAULT_MAP_OPTIONS, normalizeMapOptions } from 'utils/maps/mapTile.utils';
import {
  DEFAULT_DISPLAY_THEME,
  normalizeAccessibilityColorMode,
  normalizeDisplayTheme,
} from 'utils/themes/theme.utils';

export const normalizeAccessibilityOptions = (
  accessibilityOptions = DEFAULT_ACCESSIBILITY_OPTIONS,
) => {
  if (!accessibilityOptions || typeof accessibilityOptions !== 'object') {
    return { ...DEFAULT_ACCESSIBILITY_OPTIONS };
  }

  return {
    ...DEFAULT_ACCESSIBILITY_OPTIONS,
    colorMode: normalizeAccessibilityColorMode(accessibilityOptions.colorMode),
  };
};

export const normalizeDisplayOptions = (options = DEFAULT_DISPLAY_OPTIONS) => ({
  displayTheme: normalizeDisplayTheme(options.displayTheme || DEFAULT_DISPLAY_THEME),
  mapOptions: normalizeMapOptions(options.mapOptions || DEFAULT_MAP_OPTIONS),
  chartOptions: normalizeChartOptions(options.chartOptions || DEFAULT_CHART_OPTIONS),
  accessibilityOptions: normalizeAccessibilityOptions(
    options.accessibilityOptions || DEFAULT_ACCESSIBILITY_OPTIONS,
  ),
});

export const readStoredOptions = (storageKey, defaults, normalizer) => {
  if (typeof window === 'undefined') return { ...defaults };

  try {
    const rawOptions = window.localStorage.getItem(storageKey);
    if (!rawOptions) return { ...defaults };

    return normalizer(JSON.parse(rawOptions));
  } catch (error) {
    return { ...defaults };
  }
};

export const persistOptions = (storageKey, options) => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(storageKey, JSON.stringify(options));
  } catch (error) {}
};

export const readStoredValue = (storageKey, defaultValue, normalizer) => {
  if (typeof window === 'undefined') return defaultValue;

  try {
    const rawValue = window.localStorage.getItem(storageKey);
    if (!rawValue) return defaultValue;

    return normalizer(JSON.parse(rawValue));
  } catch (error) {
    return defaultValue;
  }
};

/** A known navigation style, else the default (Merlin Slate). */
export const normalizeNavigationStyle = (value) =>
  Object.values(NAVIGATION_STYLES).includes(value) ? value : DEFAULT_NAVIGATION_STYLE;

/** The navigation style saved on this device. */
export const readStoredNavigationStyle = () =>
  readStoredValue(NAVIGATION_STYLE_STORAGE_KEY, DEFAULT_NAVIGATION_STYLE, normalizeNavigationStyle);

export const readStoredDisplayOptions = () => ({
  displayTheme: readStoredValue(
    DISPLAY_THEME_STORAGE_KEY,
    DEFAULT_DISPLAY_THEME,
    normalizeDisplayTheme,
  ),
  mapOptions: readStoredOptions(MAP_OPTIONS_STORAGE_KEY, DEFAULT_MAP_OPTIONS, normalizeMapOptions),
  chartOptions: readStoredOptions(
    CHART_OPTIONS_STORAGE_KEY,
    DEFAULT_CHART_OPTIONS,
    normalizeChartOptions,
  ),
  accessibilityOptions: readStoredOptions(
    ACCESSIBILITY_OPTIONS_STORAGE_KEY,
    DEFAULT_ACCESSIBILITY_OPTIONS,
    normalizeAccessibilityOptions,
  ),
});

export const persistDisplayOptions = (options) => {
  const normalizedOptions = normalizeDisplayOptions(options);

  persistOptions(DISPLAY_THEME_STORAGE_KEY, normalizedOptions.displayTheme);
  persistOptions(MAP_OPTIONS_STORAGE_KEY, normalizedOptions.mapOptions);
  persistOptions(CHART_OPTIONS_STORAGE_KEY, normalizedOptions.chartOptions);
  persistOptions(ACCESSIBILITY_OPTIONS_STORAGE_KEY, normalizedOptions.accessibilityOptions);
};

export const clearStoredDisplayOptions = () => {
  if (typeof window === 'undefined') return;

  [
    DISPLAY_THEME_STORAGE_KEY,
    MAP_OPTIONS_STORAGE_KEY,
    CHART_OPTIONS_STORAGE_KEY,
    ACCESSIBILITY_OPTIONS_STORAGE_KEY,
  ].forEach((storageKey) => {
    try {
      window.localStorage.removeItem(storageKey);
    } catch (error) {}
  });
};

export const getDisplayStyleForAccessibilityColorMode = (colorMode) => {
  const normalizedColorMode = normalizeAccessibilityColorMode(colorMode);

  return (
    ACCESSIBILITY_DISPLAY_STYLE_BY_COLOR_MODE[normalizedColorMode] ||
    ACCESSIBILITY_DISPLAY_STYLE_BY_COLOR_MODE[ACCESSIBILITY_COLOR_MODES.DEFAULT]
  );
};
